import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { PASSPORT_BUILDER_SYNTHETIC_TAB_ID } from './constants'

/** Which slice of `passport_grid_template` the active tab edits. */
export type PassportTabScope =
  | { kind: 'synthetic' }
  | { kind: 'server'; groupId: number }
  | { kind: 'pending'; clientTabId: string }

/**
 * @param listGroupIds — When the synthetic tab is active, cells assigned to these server group ids
 *   (LIST / перечни) still render here because LIST groups are not shown as canvas tabs.
 */
export function itemBelongsToTabScope(
  item: GridFieldType,
  scope: PassportTabScope,
  listGroupIds?: Set<number>,
): boolean {
  if (scope.kind === 'synthetic') {
    const g = item.group_id
    const hasGroup = g != null && Number.isFinite(Number(g))
    if (hasGroup) {
      const gid = Number(g)
      if (listGroupIds?.has(gid)) return true
      return false
    }
    return !item.builder_tab_ref
  }
  if (scope.kind === 'server') return item.group_id === scope.groupId
  return item.builder_tab_ref === scope.clientTabId
}

export function stampItemForTabScope(
  item: GridFieldType,
  scope: PassportTabScope,
): GridFieldType {
  const next: GridFieldType = { ...item }
  delete next.builder_tab_ref
  if (scope.kind === 'synthetic') {
    delete next.group_id
    return next
  }
  if (scope.kind === 'server') {
    next.group_id = scope.groupId
    return next
  }
  delete next.group_id
  next.builder_tab_ref = scope.clientTabId
  return next
}

export function clearItemTabAssignment(item: GridFieldType): GridFieldType {
  const next: GridFieldType = { ...item }
  delete next.group_id
  delete next.builder_tab_ref
  return next
}

export function scopeForActiveTab(
  tabs: { id: string; isSynthetic?: boolean; groupId?: number }[],
  activeTabId: string,
): PassportTabScope | null {
  const tab = tabs.find((t) => t.id === activeTabId)
  if (!tab) return null
  if (tab.isSynthetic) return { kind: 'synthetic' }
  if (tab.groupId != null) return { kind: 'server', groupId: tab.groupId }
  return { kind: 'pending', clientTabId: tab.id }
}

/**
 * Tab id that should be active to show this grid item (matches {@link itemBelongsToTabScope}).
 */
export function tabIdForGridItem(item: GridFieldType, listGroupIds: Set<number>): string {
  const g = item.group_id
  const hasGroup = g != null && Number.isFinite(Number(g))
  if (hasGroup) {
    const gid = Number(g)
    if (listGroupIds.has(gid)) return PASSPORT_BUILDER_SYNTHETIC_TAB_ID
    return String(gid)
  }
  if (item.builder_tab_ref) return item.builder_tab_ref
  return PASSPORT_BUILDER_SYNTHETIC_TAB_ID
}

/** First top-level canvas field cell in the template with this inventory field id, if any. */
export function findPlacedCanvasField(
  template: GridFieldType[],
  inventoryFieldId: string,
): GridFieldType | undefined {
  return template.find(
    (f) =>
      (f.type === undefined || f.type === 'field') && String(f.id) === String(inventoryFieldId),
  )
}
